import { useState, useEffect, useRef } from 'react';
import { Star, Camera, Youtube, Users, CheckCircle, Upload, X, User } from 'lucide-react';
import { apiUrl } from '../../utils/api';
import { formatRelativeDate } from '../../utils/date';

interface EvidencePhoto {
  id: string; fileUrl: string; caption: string; createdAt: string;
}
interface Review {
  id: string; rating: number; comment: string; createdAt: string;
  user: { id: string; fullName: string; profileImageUrl?: string; title?: string };
}
interface PublicDonor {
  name: string; status: string; createdAt: string;
  items: { quantity: number; name: string; unit: string }[];
}

interface ResultsData {
  completionMessage: string | null;
  completionDate: string | null;
  videoUrl: string | null;
  evidencePhotos: EvidencePhoto[];
  reviews: Review[];
  avgRating: number;
  totalReviews: number;
  donors: PublicDonor[];
  totalDonors: number;
}

interface Props {
  campaignId: string;
  isOwner: boolean;
}

export function ResultsSection({ campaignId, isOwner }: Props) {
  const [data, setData] = useState<ResultsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [videoInput, setVideoInput] = useState('');
  const [showVideoForm, setShowVideoForm] = useState(false);
  const [uploadingEvidence, setUploadingEvidence] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user') || ''); } catch { return null; } })();

  useEffect(() => { loadResults(); }, [campaignId]);

  const loadResults = async () => {
    try {
      const res = await fetch(apiUrl(`/api/campaigns/${campaignId}/results-public`));
      const json = await res.json();
      if (json.success) setData(json.data);
    } catch { /* ignore */ }
    finally { setIsLoading(false); }
  };

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token) return;
    setIsSubmitting(true);
    try {
      const res = await fetch(apiUrl(`/api/campaigns/${campaignId}/reviews`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ rating: reviewRating, comment: reviewComment }),
      });
      const json = await res.json();
      if (json.success) { setShowReviewForm(false); setReviewComment(''); loadResults(); }
    } catch { /* ignore */ }
    finally { setIsSubmitting(false); }
  };

  const handleUploadEvidence = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    setUploadingEvidence(true);
    const fd = new FormData();
    for (let i = 0; i < Math.min(files.length, 5); i++) {
      // Compress in frontend
      const compressed = await compressImage(files[i], 400, 800);
      fd.append('evidenceImages', compressed, `evidence-${i}.jpg`);
    }

    try {
      const res = await fetch(apiUrl(`/api/campaigns/${campaignId}/evidence`), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd,
      });
      await res.json();
      loadResults();
    } catch { /* ignore */ }
    finally { setUploadingEvidence(false); e.target.value = ''; }
  };

  const handleSaveVideo = async () => {
    const token = localStorage.getItem('token');
    if (!token || !videoInput) return;
    try {
      await fetch(apiUrl(`/api/campaigns/${campaignId}/video`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ videoUrl: videoInput }),
      });
      setShowVideoForm(false);
      loadResults();
    } catch { /* ignore */ }
  };

  const getYoutubeEmbedUrl = (url: string) => {
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([^&?/]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  if (isLoading) return <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div></div>;
  if (!data) return null;

  const alreadyReviewed = data.reviews.some(r => r.user.id === currentUser?.id);

  return (
    <div className="space-y-6">
      {/* Completion summary */}
      <div className="bg-green-50 border border-green-200 rounded-xl p-5">
        <div className="flex items-center space-x-3 mb-3">
          <CheckCircle className="w-6 h-6 text-green-600" />
          <h3 className="text-lg font-bold text-green-900">Campaña Finalizada</h3>
          {data.avgRating > 0 && (
            <div className="flex items-center space-x-1 ml-auto">
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <span className="text-sm font-bold text-gray-900">{data.avgRating}</span>
              <span className="text-xs text-gray-500">({data.totalReviews})</span>
            </div>
          )}
        </div>
        {data.completionMessage && (
          <p className="text-sm text-green-800 italic mb-2">"{data.completionMessage}"</p>
        )}
        <p className="text-xs text-green-600">{data.totalDonors} personas aportaron</p>
      </div>

      {/* Video */}
      {data.videoUrl && getYoutubeEmbedUrl(data.videoUrl) && (
        <div className="rounded-xl overflow-hidden border border-gray-200">
          <iframe
            src={getYoutubeEmbedUrl(data.videoUrl)!}
            className="w-full aspect-video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="Video de agradecimiento"
          />
        </div>
      )}

      {/* Owner: add video */}
      {isOwner && !data.videoUrl && (
        <div className="border border-dashed border-gray-300 rounded-xl p-4">
          {showVideoForm ? (
            <div className="flex gap-2">
              <input type="url" value={videoInput} onChange={(e) => setVideoInput(e.target.value)}
                placeholder="https://youtube.com/watch?v=..." className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              <button onClick={handleSaveVideo} className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700">Guardar</button>
              <button onClick={() => setShowVideoForm(false)} className="p-2 text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <button onClick={() => setShowVideoForm(true)} className="flex items-center space-x-2 text-sm text-indigo-600 font-medium mx-auto">
              <Youtube className="w-4 h-4" /><span>Agregar video de agradecimiento (YouTube)</span>
            </button>
          )}
        </div>
      )}

      {/* Evidence photos */}
      {data.evidencePhotos.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center"><Camera className="w-4 h-4 mr-2" />Evidencia fotográfica</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {data.evidencePhotos.map(photo => (
              <div key={photo.id} className="relative aspect-square rounded-lg overflow-hidden border border-gray-200">
                <img src={photo.fileUrl} alt={photo.caption || 'Evidencia'} className="w-full h-full object-cover" />
                {photo.caption && (
                  <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-2 py-1">
                    <p className="text-[10px] text-white truncate">{photo.caption}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Owner: upload evidence */}
      {isOwner && data.evidencePhotos.length < 5 && (
        <div className="border border-dashed border-gray-300 rounded-xl p-4 text-center">
          <button onClick={() => fileRef.current?.click()} disabled={uploadingEvidence}
            className="flex items-center space-x-2 text-sm text-indigo-600 font-medium mx-auto disabled:opacity-50">
            {uploadingEvidence ? <span>Subiendo...</span> : <><Upload className="w-4 h-4" /><span>Subir fotos de evidencia ({data.evidencePhotos.length}/5)</span></>}
          </button>
          <p className="text-[10px] text-gray-400 mt-1">Máx 400KB por imagen, se comprime automáticamente</p>
          <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleUploadEvidence} className="hidden" />
        </div>
      )}

      {/* Public donors list */}
      {data.donors.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center"><Users className="w-4 h-4 mr-2" />Quienes aportaron ({data.totalDonors})</h4>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {data.donors.map((donor, i) => (
              <div key={i} className="flex items-center justify-between text-sm bg-gray-50 rounded-lg px-3 py-2">
                <div>
                  <span className="font-medium text-gray-900">{donor.name}</span>
                  <span className="text-xs text-gray-400 ml-2">{formatRelativeDate(donor.createdAt)}</span>
                </div>
                <div className="text-xs text-gray-500">
                  {donor.items.map((item, j) => <span key={j}>{item.quantity} {item.unit} {item.name}{j < donor.items.length - 1 ? ', ' : ''}</span>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reviews */}
      <div>
        <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
          <Star className="w-4 h-4 mr-2" />Reseñas de la comunidad
          {data.avgRating > 0 && <span className="ml-2 text-xs text-gray-500">({data.avgRating} promedio)</span>}
        </h4>

        {data.reviews.length === 0 && !showReviewForm && (
          <p className="text-sm text-gray-500 text-center py-4">Aún no hay reseñas. Sé el primero en opinar.</p>
        )}

        {data.reviews.map(review => (
          <div key={review.id} className="border-b border-gray-100 pb-3 mb-3 last:border-b-0">
            <div className="flex items-center space-x-2 mb-1">
              <div className="w-6 h-6 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center flex-shrink-0">
                {review.user.profileImageUrl ? (
                  <img src={review.user.profileImageUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-3 h-3 text-gray-400" />
                )}
              </div>
              <span className="text-sm font-medium text-gray-900">{review.user.fullName}</span>
              {review.user.title && <span className="text-xs text-gray-500">· {review.user.title}</span>}
              <div className="flex ml-auto">
                {[1, 2, 3, 4, 5].map(s => (
                  <Star key={s} className={`w-3 h-3 ${s <= review.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} />
                ))}
              </div>
            </div>
            <p className="text-sm text-gray-700 ml-8">{review.comment}</p>
            <p className="text-[10px] text-gray-400 ml-8 mt-1">{formatRelativeDate(review.createdAt)}</p>
          </div>
        ))}

        {/* Write review */}
        {currentUser && !alreadyReviewed && !isOwner && (
          showReviewForm ? (
            <form onSubmit={handleSubmitReview} className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center space-x-1">
                {[1, 2, 3, 4, 5].map(s => (
                  <button key={s} type="button" onClick={() => setReviewRating(s)}>
                    <Star className={`w-6 h-6 transition-colors ${s <= reviewRating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300 hover:text-yellow-300'}`} />
                  </button>
                ))}
              </div>
              <textarea value={reviewComment} onChange={(e) => setReviewComment(e.target.value)}
                placeholder="¿Cómo fue tu experiencia? ¿Se cumplió el objetivo? (mín. 10 caracteres)"
                rows={3} maxLength={1000}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500" />
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowReviewForm(false)} className="px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg">Cancelar</button>
                <button type="submit" disabled={isSubmitting || reviewComment.length < 10}
                  className="px-3 py-1.5 text-sm text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium">
                  {isSubmitting ? 'Enviando...' : 'Publicar reseña'}
                </button>
              </div>
            </form>
          ) : (
            <button onClick={() => setShowReviewForm(true)} className="w-full py-3 border border-dashed border-gray-300 rounded-lg text-sm text-indigo-600 font-medium hover:bg-indigo-50 transition-colors">
              Dejar una reseña
            </button>
          )
        )}

        {!currentUser && (
          <p className="text-xs text-gray-500 text-center py-2">Inicia sesión para dejar una reseña</p>
        )}
      </div>
    </div>
  );
}

async function compressImage(file: File, maxSizeKB: number, maxDim: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let w = img.width, h = img.height;
        if (w > maxDim || h > maxDim) {
          if (w > h) { h = (h / w) * maxDim; w = maxDim; }
          else { w = (w / h) * maxDim; h = maxDim; }
        }
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
        let quality = 0.8;
        const tryCompress = () => {
          canvas.toBlob((blob) => {
            if (!blob) return reject(new Error('Compression failed'));
            if (blob.size <= maxSizeKB * 1024 || quality <= 0.1) resolve(blob);
            else { quality -= 0.1; tryCompress(); }
          }, 'image/jpeg', quality);
        };
        tryCompress();
      };
      img.onerror = reject;
      img.src = e.target!.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
